/**
 * Curated 1960s Music — 30+ questions, hand-authored, audience-calibrated.
 *
 * Bias: Beatles, Motown, Beach Boys, Aretha, Sinatra-era, Dylan, Stones,
 * Supremes, Cash, Otis, Sam Cooke. Recognizable hits, household-name
 * artists, era-defining moments. No deep-cuts, no gotchas.
 *
 * Each blurb is a one-line friendly aside — what you'd actually say over
 * coffee, not a Wikipedia footnote.
 */

import type { TriviaQuestion } from './types';

export const CURATED_MUSIC_60S: TriviaQuestion[] = [
  {
    id: 'c-m60-01',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Beatles album cover shows the band on a zebra crossing?',
    options: ['Abbey Road', 'Let It Be', 'Help!', 'Rubber Soul'],
    correctIndex: 0,
    blurb: 'Abbey Road, 1969 — that crosswalk is still a London tourist stop today.',
    source: 'curated',
  },
  {
    id: 'c-m60-02',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Aretha Franklin spelled out which word in her 1967 anthem?',
    options: ['LOVE', 'SOUL', 'RESPECT', 'WOMAN'],
    correctIndex: 2,
    blurb: 'Recorded in Muscle Shoals — and it absolutely belonged to her, not Otis.',
    source: 'curated',
  },
  {
    id: 'c-m60-03',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'The Beach Boys harmonized about which sunny pastime in 1963?',
    options: ['Surfin\' USA', 'Hotel California', 'California Dreamin\'', 'Goin\' to the Chapel'],
    correctIndex: 0,
    blurb: 'Brian Wilson somehow heard whole symphonies on a Pacific shoreline.',
    source: 'curated',
  },
  {
    id: 'c-m60-04',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Motown group recorded "My Girl" in 1964?',
    options: ['The Four Tops', 'The Temptations', 'The Miracles', 'The Supremes'],
    correctIndex: 1,
    blurb: 'David Ruffin on lead — Smokey Robinson wrote it for him in one sitting.',
    source: 'curated',
  },
  {
    id: 'c-m60-05',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Frank Sinatra famously did it which way?',
    options: ['His Way', 'The Hard Way', 'The Long Way', 'Their Way'],
    correctIndex: 0,
    blurb: 'Paul Anka wrote the English lyrics on a Manhattan typewriter at 3 a.m.',
    source: 'curated',
  },
  {
    id: 'c-m60-06',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Bob Dylan plugged in and went electric at which 1965 festival?',
    options: ['Monterey Pop', 'Woodstock', 'Newport Folk', 'Isle of Wight'],
    correctIndex: 2,
    blurb: 'Half the crowd cheered, half booed — both halves remembered it for life.',
    source: 'curated',
  },
  {
    id: 'c-m60-07',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'The Supremes were fronted by which legendary singer?',
    options: ['Gladys Knight', 'Tina Turner', 'Diana Ross', 'Etta James'],
    correctIndex: 2,
    blurb: 'Twelve number-one singles on Motown — nobody else came close in the decade.',
    source: 'curated',
  },
  {
    id: 'c-m60-08',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which 1969 festival drew nearly half a million people to a New York dairy farm?',
    options: ['Coachella', 'Glastonbury', 'Woodstock', 'Live Aid'],
    correctIndex: 2,
    blurb: 'They called it three days of peace and music — and it mostly was.',
    source: 'curated',
  },
  {
    id: 'c-m60-09',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: '"(I Can\'t Get No) Satisfaction" is a 1965 hit by which band?',
    options: ['The Who', 'The Rolling Stones', 'The Kinks', 'The Animals'],
    correctIndex: 1,
    blurb: 'Keith Richards wrote that riff in his sleep — literally, on a bedside tape recorder.',
    source: 'curated',
  },
  {
    id: 'c-m60-10',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which 1967 Beatles album opens with a brass-band burst introducing the band?',
    options: [
      'Revolver',
      'Sgt. Pepper\'s Lonely Hearts Club Band',
      'Magical Mystery Tour',
      'The White Album',
    ],
    correctIndex: 1,
    blurb: 'Sgt. Pepper — Rolling Stone has called it the greatest album ever made more than once.',
    source: 'curated',
  },
  {
    id: 'c-m60-11',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Otis Redding sat on a dock of which bay?',
    options: ['Tampa Bay', 'San Francisco Bay', 'Chesapeake Bay', 'Mobile Bay'],
    correctIndex: 1,
    blurb: 'He recorded it three days before his plane went down — Steve Cropper finished the mix.',
    source: 'curated',
  },
  {
    id: 'c-m60-12',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which singer famously wore black and walked the line?',
    options: ['Willie Nelson', 'Johnny Cash', 'Glen Campbell', 'Roy Orbison'],
    correctIndex: 1,
    blurb: 'He wore black for the working poor — said so plainly on stage at Folsom in \'68.',
    source: 'curated',
  },
  {
    id: 'c-m60-13',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Sam Cooke sang that a change was gonna do what?',
    options: ['Stay', 'Come', 'Pass', 'Hold'],
    correctIndex: 1,
    blurb: '"A Change Is Gonna Come" — written after a Holiday Inn turned him away in 1963.',
    source: 'curated',
  },
  {
    id: 'c-m60-14',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Simon & Garfunkel\'s 1968 hit asked Mrs. Robinson where she had gone — which actress played Mrs. Robinson on film?',
    options: ['Anne Bancroft', 'Faye Dunaway', 'Audrey Hepburn', 'Katharine Ross'],
    correctIndex: 0,
    blurb: 'Anne Bancroft — only six years older than Dustin Hoffman, makeup did the rest.',
    source: 'curated',
  },
  {
    id: 'c-m60-15',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Jimi Hendrix closed Woodstock with an unforgettable version of which song?',
    options: [
      'The Star-Spangled Banner',
      'Stairway to Heaven',
      'America the Beautiful',
      'Purple Haze',
    ],
    correctIndex: 0,
    blurb: 'Played for the few thousand who stayed past dawn — and recorded for history.',
    source: 'curated',
  },
  {
    id: 'c-m60-16',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Beatle wrote and sang lead on "Yesterday"?',
    options: ['John Lennon', 'Paul McCartney', 'George Harrison', 'Ringo Starr'],
    correctIndex: 1,
    blurb: 'McCartney woke up with the melody and called it "Scrambled Eggs" until lyrics came.',
    source: 'curated',
  },
  {
    id: 'c-m60-17',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Motown family group, fronted by an 11-year-old Michael, broke through in 1969?',
    options: ['The Jacksons', 'The Jackson 5', 'The Osmonds', 'The Sylvers'],
    correctIndex: 1,
    blurb: 'Four number-one singles in their first year — record-label history.',
    source: 'curated',
  },
  {
    id: 'c-m60-18',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'The Doors were fronted by which singer?',
    options: ['Mick Jagger', 'Jim Morrison', 'Jim Croce', 'Janis Joplin'],
    correctIndex: 1,
    blurb: 'A film student who wrote poetry in a Venice Beach hotel before the band even existed.',
    source: 'curated',
  },
  {
    id: 'c-m60-19',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which 1966 Beach Boys album is widely considered Brian Wilson\'s masterpiece?',
    options: ['Surfin\' Safari', 'All Summer Long', 'Pet Sounds', 'Smiley Smile'],
    correctIndex: 2,
    blurb: 'Paul McCartney said hearing it changed how he wrote the Beatles\' next record.',
    source: 'curated',
  },
  {
    id: 'c-m60-20',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which 1962 Tony Bennett standard celebrates a California city?',
    options: [
      'I Left My Heart in San Francisco',
      'California Here I Come',
      'Hotel California',
      'San Francisco (Be Sure to Wear Flowers)',
    ],
    correctIndex: 0,
    blurb: 'He sang it nightly for the rest of his career — the city made him an honorary citizen.',
    source: 'curated',
  },
  {
    id: 'c-m60-21',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Janis Joplin was the lead singer of which band before going solo?',
    options: [
      'Jefferson Airplane',
      'Big Brother and the Holding Company',
      'Cream',
      'The Grateful Dead',
    ],
    correctIndex: 1,
    blurb: 'Her Monterey Pop set in 1967 made the rest of America notice — she stole the festival.',
    source: 'curated',
  },
  {
    id: 'c-m60-22',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Patsy Cline famously sang about going what kind of "crazy"?',
    options: ['Just', 'Quite', 'Hopeless', 'Plain'],
    correctIndex: 0,
    blurb: '"Crazy" — written by a young Willie Nelson, recorded in one take.',
    source: 'curated',
  },
  {
    id: 'c-m60-23',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which British band sang "House of the Rising Sun" in 1964?',
    options: ['The Animals', 'The Hollies', 'Herman\'s Hermits', 'The Zombies'],
    correctIndex: 0,
    blurb: 'Eric Burdon\'s vocal — recorded in one 15-minute take, mostly because the studio was booked.',
    source: 'curated',
  },
  {
    id: 'c-m60-24',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Petula Clark sang about going to which place "where all the lights are bright"?',
    options: ['Uptown', 'Downtown', 'Midtown', 'Hometown'],
    correctIndex: 1,
    blurb: 'A British singer\'s biggest American hit — written about Times Square at night.',
    source: 'curated',
  },
  {
    id: 'c-m60-25',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Memphis label was home to Otis Redding, Booker T., and Sam & Dave?',
    options: ['Motown', 'Atlantic', 'Stax', 'Chess'],
    correctIndex: 2,
    blurb: 'Stax — recorded inside a converted movie theatre on McLemore Avenue.',
    source: 'curated',
  },
  {
    id: 'c-m60-26',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Detroit-based label launched the Supremes, the Temptations, and Stevie Wonder?',
    options: ['Sun', 'Chess', 'Motown', 'Stax'],
    correctIndex: 2,
    blurb: 'Berry Gordy ran the building like an assembly line — and it made history.',
    source: 'curated',
  },
  {
    id: 'c-m60-27',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'The Mamas & the Papas dreamed of which state on a winter\'s day?',
    options: ['Florida', 'California', 'Hawaii', 'Arizona'],
    correctIndex: 1,
    blurb: '"California Dreamin\'" — written in a New York hotel during a brutal cold snap.',
    source: 'curated',
  },
  {
    id: 'c-m60-28',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which 1969 Aretha Franklin album showed her in queenly profile and a regal silk gown?',
    options: ['I Never Loved a Man', 'Lady Soul', 'Soul \'69', 'Spirit in the Dark'],
    correctIndex: 2,
    blurb: '"Soul \'69" — recorded with a small jazz combo, very different from her Atlantic hits.',
    source: 'curated',
  },
  {
    id: 'c-m60-29',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'The 1965 song "Mr. Tambourine Man" was originally written by which folk songwriter?',
    options: ['Pete Seeger', 'Joan Baez', 'Bob Dylan', 'Phil Ochs'],
    correctIndex: 2,
    blurb: 'The Byrds had the radio hit; Dylan\'s version came out later the same year.',
    source: 'curated',
  },
  {
    id: 'c-m60-30',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Roy Orbison\'s 1964 hit was about a "Pretty" what?',
    options: ['Penny', 'Girl', 'Lady', 'Woman'],
    correctIndex: 3,
    blurb: '"Oh, Pretty Woman" — that growl in the chorus apparently startled the studio engineer.',
    source: 'curated',
  },
  {
    id: 'c-m60-31',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'Which Beatle famously played the sitar on "Norwegian Wood"?',
    options: ['John Lennon', 'Paul McCartney', 'George Harrison', 'Ringo Starr'],
    correctIndex: 2,
    blurb: 'Harrison studied with Ravi Shankar — and changed Western pop music in the process.',
    source: 'curated',
  },
  {
    id: 'c-m60-32',
    category: '1960s Music',
    categorySlug: 'music-60s',
    prompt: 'The Four Seasons were fronted by which falsetto-voiced singer?',
    options: ['Bobby Darin', 'Frankie Valli', 'Bobby Rydell', 'Frankie Avalon'],
    correctIndex: 1,
    blurb: 'Years later, Broadway turned the whole story into "Jersey Boys."',
    source: 'curated',
  },
];
